import i18n from '@/utils/i18n';
import OfflineSyncIcon from '@assets/icons/offline-sync.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { LocalScanCatalogRefreshTrigger } from '@/utils/sentry/attributes';
import { Pressable, View } from 'react-native';
import { ScannerStaleness } from '@/hooks/offline-mode/use-local-scan-data';
import { SvgProps } from 'react-native-svg';

interface Props {
  iconProps?: SvgProps;
  onPress: (trigger?: LocalScanCatalogRefreshTrigger) => void;
  staleness: ScannerStaleness;
}

export const LocalScannerDownloadButton: React.FC<Props> = ({
  onPress,
  staleness,
  iconProps,
}) => {
  const [styles, theme] = useStyles();

  const dotStyle =
    staleness === ScannerStaleness.Stale7Days
      ? [styles.dot, styles.dotError]
      : staleness === ScannerStaleness.Stale3Days
        ? [styles.dot, styles.dotWarning]
        : null;

  const accessibilityLabel =
    staleness === ScannerStaleness.Stale7Days
      ? i18n.t('scan.sync_catalog_stale_N_days', { count: 7 })
      : staleness === ScannerStaleness.Stale3Days
        ? i18n.t('scan.sync_catalog_stale_N_days', { count: 3 })
        : i18n.t('scan.sync_catalog');

  const syncTrigger =
    staleness === ScannerStaleness.Stale7Days
      ? LocalScanCatalogRefreshTrigger.ManualStale7Days
      : staleness === ScannerStaleness.Stale3Days
        ? LocalScanCatalogRefreshTrigger.ManualStale3Days
        : LocalScanCatalogRefreshTrigger.ManualFresh;

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      onPress={() => onPress(syncTrigger)}
      style={styles.container}
    >
      <OfflineSyncIcon
        height={24}
        stroke={theme.sk.color.icon.primary}
        width={24}
        {...iconProps}
      />
      {dotStyle ? (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={dotStyle}
        />
      ) : null}
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  dot: {
    borderRadius: theme.sk.spacing.spaceXsmall,
    height: theme.sk.spacing.spaceSmall,
    position: 'absolute',
    right: theme.sk.spacing.spaceXsmall,
    top: theme.sk.spacing.spaceXsmall,
    width: theme.sk.spacing.spaceSmall,
  },
  dotError: {
    backgroundColor: theme.sk.color.text.error,
  },
  dotWarning: {
    backgroundColor: theme.sk.color.icon.warning.primary,
  },
}));
